import { Controller } from "stimulus";

// Waiting-room countdown -> reveal a "chat closes in" countdown once the content finishes;
// redirect to the episode when a premiere's window closes. Shared by both live streams and
// premieres now - a stream has no window-close redirect (mark_live!/mark_idle! drive its
// transitions via turbo_stream_from instead), so the view leaves feedItemUrl blank for a stream
// and watchWindowEnd below no-ops on its own guard. endsAtMs/playbackEndsAtMs are real for either
// kind: a stream's chat runs on for its own afterparty past the moment the creator ended it.
// At T-0 the two kinds diverge (see renderCountdown): a premiere is clock-driven, so reaching
// the target reloads into the playing state; a stream is webhook-driven, so reaching starts_at
// says nothing about whether the creator has actually gone live - kindValue picks the branch.
// waitingValue is the ticking-countdown gate, not the same thing as the page's own "waiting room"
// content state: a deferred premiere or a stream stuck past its own starts_at also render
// waiting-room copy, but with no live target to count down to, so the view leaves waitingValue
// false for those - see live_pages/show.html.erb's countdown_target_ms.
export default class ShowController extends Controller {
  static targets = ["countdown", "countdownLabel", "countdownOverdue", "chatClose", "chatCloseBar"];
  static values = {
    kind: String,
    startsAtMs: Number,
    endsAtMs: Number,
    serverNowMs: Number,
    playbackEndsAtMs: Number,
    feedItemUrl: String,
    waiting: Boolean,
  };

  connect() {
    // Offset a skewed client clock onto server time (minted at render).
    this.clockOffset = this.serverNowMsValue - Date.now();

    if (this.waitingValue) {
      this.renderCountdown();
      this.countdownInterval = window.setInterval(() => this.renderCountdown(), 1000);
    }

    this.watchAfterparty();
    this.watchWindowEnd();
  }

  disconnect() {
    if (this.countdownInterval) window.clearInterval(this.countdownInterval);
    if (this.windowEndInterval) window.clearInterval(this.windowEndInterval);
    if (this.afterpartyInterval) window.clearInterval(this.afterpartyInterval);
  }

  now() {
    return Date.now() + this.clockOffset;
  }

  renderCountdown() {
    const remaining = this.startsAtMsValue - this.now();
    if (remaining <= 0) {
      if (this.countdownInterval) window.clearInterval(this.countdownInterval);

      if (this.kindValue === "premiere") {
        // Clock-driven: reaching the target time IS the state change, so reload into it
        // (fresh signed URL + player).
        window.location.reload();
        return;
      }

      // Webhook-driven: reaching starts_at does not mean the creator has actually gone live
      // yet - mark_live!'s turbo_stream broadcast (subscribed above) handles that transition
      // whenever it really happens. Swap to the same static "Starting soon" markup a fresh
      // load in this state would render, rather than reload-looping on a stale target.
      if (this.hasCountdownLabelTarget) this.countdownLabelTarget.classList.add("hidden");
      if (this.hasCountdownTarget) this.countdownTarget.classList.add("hidden");
      if (this.hasCountdownOverdueTarget) this.countdownOverdueTarget.classList.remove("hidden");
      return;
    }
    if (this.hasCountdownTarget) this.countdownTarget.textContent = this.format(remaining);
  }

  // Reveal the "chat closes in M:SS" line and count it down to the afterparty end - both when the page
  // loads mid-afterparty and when a premiere's video finishes while the viewer is watching (no reload).
  watchAfterparty() {
    if (!this.playbackEndsAtMsValue || !this.hasChatCloseBarTarget) return;

    const tick = () => {
      if (this.now() < this.playbackEndsAtMsValue) return;
      this.chatCloseBarTarget.classList.remove("hidden");
      if (this.hasChatCloseTarget) {
        this.chatCloseTarget.textContent = this.format(Math.max(0, this.endsAtMsValue - this.now()));
      }
    };

    tick();
    this.afterpartyInterval = window.setInterval(tick, 1000);
  }

  // Window end can be hours out; setTimeout is unreliable over long spans/sleep, so poll the offset clock.
  watchWindowEnd() {
    if (!this.feedItemUrlValue || !this.endsAtMsValue) return;

    this.windowEndInterval = window.setInterval(() => {
      if (this.now() >= this.endsAtMsValue) {
        window.clearInterval(this.windowEndInterval);
        window.location.assign(this.feedItemUrlValue);
      }
    }, 1000);
  }

  // MM:SS, widening to H:MM:SS once an hour or more remains.
  format(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}:${this.pad(minutes)}:${this.pad(seconds)}`;
    return `${this.pad(minutes)}:${this.pad(seconds)}`;
  }

  pad(value) {
    return String(value).padStart(2, "0");
  }
}
